// File: components/Overlay.tsx
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { COLORS } from './config/colors';

interface OverlayProps {
  title: string;
  subtitle: string;
  buttonText: string;
  onClick: () => void;
  children: React.ReactNode;
  titleColor?: string;
}

export const Overlay: React.FC<OverlayProps> = ({ title, subtitle, buttonText, onClick, children, titleColor }) => {
  const COUNTDOWN_BUTTONS = ["FINISH PERFORMANCE", "START SHOW", "START PRACTICE", "RETRY"];
  const shouldCountdown = COUNTDOWN_BUTTONS.includes(buttonText.toUpperCase());

  const [countdown, setCountdown] = useState<number>(3);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isMounted = useRef(true);

  useEffect(() => {
    isMounted.current = true;
    return () => { isMounted.current = false; };
  }, []);

  const startAction = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }
    onClick();
  }, [onClick]);

  useEffect(() => {
    if (!shouldCountdown) return;

    if (countdown <= 0) {
      if (isMounted.current) startAction();
      return;
    }

    timerRef.current = setTimeout(() => {
      if (isMounted.current) setCountdown(prev => prev - 1);
    }, 1000);

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [countdown, startAction, shouldCountdown]);

  return (
    <div style={{
      position: "absolute", top: 0, left: 0, width: "100%", height: "100%",
      display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
      backgroundColor: "transparent", /* Fully Transparent Background */
      color: "white", textAlign: "center", fontFamily: "'Courier New', Courier, monospace",
      pointerEvents: "auto"
    }}>
      <h1 style={{
        fontSize: "48px",
        color: titleColor || COLORS.player,
        textShadow: "0px 0px 20px black, 4px 4px 0px #00ffff", /* Heavy shadow for contrast */
        margin: 0
      }}>
        {title}
      </h1>
      <h2 style={{
        color: COLORS.sword,
        marginTop: "10px",
        textShadow: "0px 2px 4px black"
      }}>
        {subtitle}
      </h2>

      {/* Children container: Full width, no background, no restriction */}
      <div style={{
        marginTop: "20px",
        width: "100%",
        display: "flex",
        justifyContent: "center",
        backgroundColor: "transparent",
      }}>
        {children}
      </div>

      <button
        onClick={startAction}
        style={{
          marginTop: "30px", padding: "15px 40px", fontSize: "24px",
          backgroundColor: "transparent",
          color: "white",
          border: `3px solid ${COLORS.player}`,
          cursor: "pointer",
          fontFamily: "monospace", boxShadow: "0 0 20px #ff007f",
          minWidth: '250px',
          textShadow: "0 1px 2px black",
          backdropFilter: "blur(4px)"
        }}
      >
        {shouldCountdown ? `${buttonText} [${countdown > 0 ? countdown : 0}]` : buttonText}
      </button>
    </div>
  );
};